import React, { useRef, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, message, Modal, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./subs_data_so.css";
import { SalesDetailDataType } from '../../components/hoc_substitution';
import axios from 'axios';

interface DataType {
    se_no: string;
    so_no: string;
    sales_date: string;
    sales_person: string;
}

interface SubsDataSOProps {
    dataSource: DataType[];
    handleImport: (selectedItems: Pick<SalesDetailDataType, 'item_code' | 'item_name'>[]) => void;
}

type DataIndex = keyof DataType;

interface SubsDataSOProps {
    dataSource: DataType[];
}


const SubsDataSO: React.FC<SubsDataSOProps> = ({ dataSource, handleImport }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [detailData, setDetailData] = useState<SalesDetailDataType[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedSeNo, setSelectedSeNo] = useState<string | null>(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const handleRowDoubleClick = async (record: DataType) => {
        setSelectedSeNo(record.se_no);
        setIsDetailModalOpen(true);
        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/substitution/sales-detail/${encodeURIComponent(record.se_no)}`);
            setDetailData(response.data);
        } catch (error) {
            console.error("Failed to fetch sales detail data:", error);
            message.error("Gagal mengambil data detail SO.");
        } finally {
            setLoading(false);
        }
    };

    const handleCloseDetailModal = () => {
        setIsDetailModalOpen(false);
        setDetailData([]);
        setSelectedSeNo(null);
        setSelectedRowKeys([]);
    };

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const handleImportClick = () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Silakan pilih minimal satu item untuk diimpor.');
            return;
        }
        const selectedItems = detailData.filter(item => selectedRowKeys.includes(item.id));
        const itemsToImport = selectedItems.map(item => ({
            item_code: item.item_code,
            item_name: item.item_name,
        }));
        handleImport(itemsToImport);
        handleCloseDetailModal();
    };

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: 'SE No',
            dataIndex: 'se_no',
            key: 'se_no',
            sorter: (a, b) => a.se_no.localeCompare(b.se_no),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('se_no'),
        },
        {
            title: 'SO No',
            dataIndex: 'so_no',
            key: 'so_no',
            sorter: (a, b) => a.so_no.localeCompare(b.so_no),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('so_no'),
        },
        {
            title: 'Date',
            dataIndex: 'sales_date',
            key: 'sales_date',
            sorter: (a, b) => a.sales_date.localeCompare(b.sales_date),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('sales_date'),
        },
        {
            title: 'Sales',
            dataIndex: 'sales_person',
            key: 'sales_person',
            sorter: (a, b) => a.sales_person.localeCompare(b.sales_person),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('sales_person'),
        },
    ];

    const detailColumns: TableColumnsType<SalesDetailDataType> = [
        {
            title: 'Item Code',
            dataIndex: 'item_code',
            key: 'item_code',
        },
        {
            title: 'Item Name',
            dataIndex: 'item_name',
            key: 'item_name',
        },
        {
            title: 'Qty',
            dataIndex: 'dmd',
            key: 'dmd',
        },
    ];

    return (

        <div id="ilqt" style={transformStyle}>
            <div className="gjs-grid-row" id="ihey">
                <div className="gjs-grid-column" id="icjw">
                    <Table<DataType>
                        columns={columns}
                        dataSource={dataSource}
                        rowKey="se_no"
                        onRow={(record) => {
                            return {
                                onDoubleClick: () => {
                                    handleRowDoubleClick(record);
                                },
                            };
                        }}
                    />
                </div>
            </div>
            <Modal
                title={`Detail for SE No: ${selectedSeNo}`}
                className="detail_data_se_no"
                open={isDetailModalOpen}
                onCancel={handleCloseDetailModal}
                footer={[
                    <Button key="back" onClick={handleCloseDetailModal}>
                        Batal
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        onClick={handleImportClick}
                        disabled={selectedRowKeys.length === 0}
                    >
                        Import Terpilih
                    </Button>,
                ]}
                width={800}
            >
                <Table<SalesDetailDataType>
                    rowSelection={rowSelection}
                    columns={detailColumns}
                    dataSource={detailData}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 7 }}
                />
            </Modal>
        </div>
    );
};

export default SubsDataSO;